<template>
  <SettingsModal
    :headline="$t('picklottery.quickpick.modal.headline')"
    icon="flash"
    is-visible
    :tracking="{
      action: 'checkout',
      topicKey: 'quick_pick_settings',
    }"
    @close="$emit('close')"
  >
    <div class="flex flex-col px-100 pb-300 gap-100">
      <template v-if="isLoading">
        <LcSkeleton height="5.75rem" radius="medium" />
        <LcSkeleton
          v-for="(_item, index) in Array.from({ length: 3 })"
          :key="index"
          height="4.25rem"
          radius="medium"
        />
      </template>

      <template v-else>
        <PickLotteryQuickPickQuantity
          v-model:value="modelQuantity"
          :max="maxAllowedQuantity"
          :min="minQuantity"
        />
        <PickLotteryQuickPickBetType
          v-if="perPlayAddOn"
          v-bind="{ lotteryId }"
          v-model:value="modelBetType"
          :add-on="perPlayAddOn"
        />
        <PickLotteryQuickPickNumbers
          v-bind="{ disabledFields, lotteryId }"
          v-model:value="modelNumbers"
          :error-message="errorMessageNumbers"
          :fields="fieldConfig"
          :lockout-lottery="config?.isLockoutLottery"
        />
        <PickLotteryQuickPickWager
          v-if="!isFixedStake && priceList"
          v-bind="{ priceList, stakes }"
          v-model:value="modelStake"
        />
      </template>
    </div>

    <template #footer>
      <template v-if="isLoading">
        <LcSkeleton height="3rem" radius="full" />
      </template>
      <LcButton v-else class="w-full" :disabled="!isValid" @click="handleConfirm">
        {{ $t('picklottery.quickpick.modal.button', selectedValues.quantity) }}
      </LcButton>
    </template>
  </SettingsModal>
</template>

<script lang="ts" setup>
import type { LcModalEmits } from '@lottocom/frontend-components'

import * as Yup from 'yup'

import type { QuickPickOptions } from '../composables/playSlip'

import { type QuickPickSettings, useLotteryStore } from '../stores/lottery'
import {
  quickPickBetType,
  quickPickNumbers,
  quickPickQuantity,
  quickPickWager,
} from '../stores/schemas/playSlip'

export type PickLotteryQuickPickModalConfirmPayload = Omit<QuickPickOptions, 'validateFn'> & {
  quantity: number
}

export type PickLotteryQuickPickModalEmits = {
  (event: 'confirm', payload: PickLotteryQuickPickModalConfirmPayload): void
}

export type PickLotteryQuickPickModalProps = {
  lotteryId: string
  maxQuantity?: number
  minQuantity?: number
}

const emit = defineEmits<LcModalEmits & PickLotteryQuickPickModalEmits>()
const {
  lotteryId: lotteryIdProp,
  maxQuantity = 100,
  minQuantity = 1,
} = defineProps<PickLotteryQuickPickModalProps>()

const { getTranslation, t } = useI18nUtils()
const {
  config,
  fieldConfig,
  isFixedStake,
  isLoading,
  lotteryId,
  maxAllowedPlays,
  perPlayAddOn,
  priceList,
  stakes,
  validatePlay,
} = usePlaySlip(lotteryIdProp)

const { quickPickSettingsByLottery, setQuickPickSettings } = useLotteryStore()

const disabledFields = computed(() => {
  if (!selectedValues.value.betType) return

  return config.value?.numbers.reduce<string[]>((acc, field) => {
    const isIgnored = field.ignoredForAddOns.some(
      (addOn) => addOn.value === selectedValues.value.betType,
    )
    return isIgnored ? [...acc, field.name] : acc
  }, [])
})
const errorMessageNumbers = computed(() => {
  if (isValid.value) return

  try {
    validationSchema.value.validateSyncAt('numbers', {
      numbers: selectedValues.value.numbers,
    })
    return
  } catch (error) {
    if (!(error instanceof Yup.ValidationError)) return
    return getTranslation({
      fallback: t(`lottery.${error.message}`),
      key: `lottery.${lotteryId}.${error.message}`,
    })
  }
})
const isValid = computed(() =>
  validationSchema.value.isValidSync({
    betType: selectedValues.value.betType,
    numbers: selectedValues.value.numbers,
    quantity: selectedValues.value.quantity,
    wager: selectedValues.value.wager,
  }),
)
const maxAllowedQuantity = computed(() => maxQuantity ?? maxAllowedPlays.value)
const modelBetType = computed({
  get: () => selectedValues.value.betType,
  set: (newValue) => {
    updateQuickPickSettings({
      betType: newValue,
    })
  },
})
const modelNumbers = computed({
  get: () => selectedValues.value.numbers,
  set: (newValue) => {
    updateQuickPickSettings({
      numbers: newValue,
    })
  },
})
const modelQuantity = computed({
  get: () => selectedValues.value.quantity,
  set: (newValue) => {
    updateQuickPickSettings({
      quantity: newValue,
    })
  },
})
const modelStake = computed({
  get: () => selectedValues.value.wager,
  set: (newValue) => {
    updateQuickPickSettings({
      wager: newValue,
    })
  },
})
const selectedValues = computed(() => quickPickSettingsByLottery(lotteryId))
const validationSchema = computed(() =>
  Yup.object({
    betType: quickPickBetType.oneOf(perPlayAddOn.value?.options ?? []),
    numbers: quickPickNumbers.test(
      'allowed-combinations',
      'validation.field.numbers.notvalidforbettype',
      (value) => validatePlay(selectedValues.value.betType, value),
    ),
    quantity: quickPickQuantity.min(minQuantity).max(maxAllowedQuantity.value),
    wager: quickPickWager,
  }),
)

const handleConfirm = () => {
  emit('confirm', selectedValues.value)
}

const updateQuickPickSettings = (newSelectedValues: Partial<QuickPickSettings>) => {
  setQuickPickSettings(lotteryId, {
    ...selectedValues.value,
    ...newSelectedValues,
  })
}

onMounted(() => {
  if (selectedValues.value.quantity <= maxQuantity) return
  updateQuickPickSettings({
    quantity: maxQuantity,
  })
})
</script>
